import React, { useMemo } from 'react';
import {
	FormInstance,
	Grid,
} from 'antd';
import { useMutation, useQuery } from 'react-query';
import { AccountTypes } from '@shared/utils/constants';
import { formatPercentage } from '@shared/utils/formatPercentage';
import { capitalize } from '@shared/utils/string';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { AddFixtureBrokerRequest } from '@api/features/fixtures/addFixtureBroker';
import type { UpdateFixtureBrokerRequest } from '@api/features/fixtures/updateFixtureBroker';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import type { BrokerInFixtureProps } from '@api/models/broker-in-fixture';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import EditableTable from '@client/components/EditableTable';
import {
	addFixtureBroker,
	createBroker,
	getBrokers,
	removeFixtureBroker,
	updateFixtureBroker,
} from '@client/lib/api';
import FormPopover from '@client/components/FormPopover';
import Button from '@client/components/Button';
import TooltipIcon from '@client/components/TooltipIcon';
import styles from './styles/useTcSectionItems.module.css';

type BrokerDataSource = BrokerInFixtureProps & {
	id: number;
};

const BrokersTable = ({
	fixtureDetails,
	refreshFixture,
	locked = false,
	tcTerms,
}: {
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>;
	refreshFixture: () => void;
	locked?: boolean;
	tcTerms: TcTermsWithIncludes,
}) => {
	const dataSource = tcTerms.Brokers.flatMap((b) => ({ ...b.BrokerInFixture, id: b.id }));

	const screens = Grid.useBreakpoint();
	const {
		data: brokers,
		refetch: refreshBrokers,
	} = useQuery(
		['brokers'],
		getBrokers,
	);

	const brokerOptions = useMemo(() => (brokers || [])
		.sort((a, b) => a.name.toString().localeCompare(b.name))
		.map((b) => ({
			label: b.name,
			value: b.id,
			// Don't allow selecting brokers that are already taken
			disabled: dataSource.some((fixtureBroker) => fixtureBroker.brokerId === b.id),
		})), [brokers, dataSource]);

	const addBrokerMutation = useMutation(
		(values: Omit<AddFixtureBrokerRequest, 'tcTermsId'>) =>
			addFixtureBroker(tcTerms.id, values),
		{ onSuccess: refreshFixture },
	);

	const updateBrokerMutation = useMutation(
		({ brokerId, values }: {
			brokerId: number;
			values: UpdateFixtureBrokerRequest['attributes'];
		}) => updateFixtureBroker(tcTerms.id, brokerId, values),
		{ onSuccess: refreshFixture },
	);

	const removeBrokerMutation = useMutation(
		(brokerId: number) => removeFixtureBroker(tcTerms.id, brokerId),
		{ onSuccess: refreshFixture },
	);

	const createBrokerMutation = useMutation(
		(name: string) => createBroker(name),
		{ onSuccess: () => refreshBrokers() },
	);

	const handleUpdateBroker = (
		brokerId: number,
		values: UpdateFixtureBrokerRequest['attributes'],
	) => updateBrokerMutation.mutateAsync({ brokerId, values });

	const handleRemoveBroker = (brokerId: number) =>
		removeBrokerMutation.mutateAsync(brokerId);

	const columns = useMemo(() => [
		{
			dataIndex: 'brokerId',
			title: 'Broker',
			editable: true,
			render: (id: number) => (brokers ?? []).find((b) => b.id === id)?.name,
			editingProps: {
				type: 'select',
				inputProps: { showSearch: true },
				options: brokerOptions,
				renderExtra: ({ form }: { form: FormInstance }) => (
					<FormPopover
						title="Create broker"
						fields={[
							{
								label: 'Name',
								name: 'name',
								type: 'text',
								required: true,
							},
						]}
						onSubmit={async (values: { name: string }) => {
							const broker = await createBrokerMutation.mutateAsync(values.name);

							if (form == null || broker == null) {
								return;
							}

							form.setFieldsValue({ brokerId: broker.id });
						}}
						buttonText="Submit"
					>
						<Button type="link" className={styles.createBrokerButton}>Create new broker</Button>
					</FormPopover>
				),
			},
		},
		{
			dataIndex: 'commission',
			title: 'Commission',
			editable: true,
			editingProps: {
				type: 'percentage',
				inputProps: {},
			},
			render: (c: number) => c != null && formatPercentage(c),
		},
		{
			dataIndex: 'paidBy',
			title: 'Paid by',
			editable: true,
			render: (a: string) => capitalize(a),
			editingProps: {
				type: 'select',
				inputProps: {},
				options: [
					{
						label: (
							<>
								Charterer
								<TooltipIcon>
									Broker is paid by the charterer.
									<br />
									Broker commission is deducted from the hire invoice.
								</TooltipIcon>
							</>
						),
						value: AccountTypes.CHARTERER,
						disabled: fixtureDetails.fixed,
					},
					{
						label: (
							<>
								Owner
								<TooltipIcon>
									Broker is paid by the owner.
									<br />
									Broker commission is
									<strong>{' not '}</strong>
									deducted from the hire invoice.
								</TooltipIcon>
							</>
						),
						value: AccountTypes.OWNER,
					},
				],
				defaultValue: AccountTypes.OWNER,
			},
		},
	], [brokerOptions, brokers, fixtureDetails, createBrokerMutation.mutateAsync]);

	return (
		<EditableTable<BrokerDataSource, 'id'>
			key={`brokers-${dataSource.length}`}
			iconButtons
			pagination={false}
			enableDelete={!locked}
			size="small"
			enableEdit={!locked}
			allowAddNew={!locked}
			allowEditRow={(record: BrokerDataSource) => {
				const hasMatchingBrokerId = fixtureDetails?.brokerInvoices.some(
					(invoice) => invoice.brokerId === record.brokerId,
				);

				if (!fixtureDetails.fixed && !hasMatchingBrokerId) {
					return true;
				}

				if (fixtureDetails.fixed && record.paidBy === AccountTypes.OWNER && !hasMatchingBrokerId) {
					return true;
				}

				return false;
			}}
			addNewText="Broker(s)"
			emptyText="No brokers"
			dataSource={dataSource}
			onSave={handleUpdateBroker}
			onAddNew={addBrokerMutation.mutateAsync}
			onDelete={handleRemoveBroker}
			editingRowClassName={styles.editingBroker}
			keyDataIndex="id"
			useCards={screens.xs}
			// @ts-ignore
			columns={columns}
		/>
	);
};

export default BrokersTable;
